import { Injectable } from '@angular/core';
import { Menu } from 'api-clients/applicant-api-client';
import { AccountingRoles } from 'api-clients/identity-server-api-client';
import { globalWindow } from 'src/shared/window-globals';
import { UserService } from '../../shared/user.service';

@Injectable()
export class NavMenuAccountingService {

  #defaultMenu: Partial<Menu> = {
    searchAliases: [],
    searchOnly: false,
    targetNewWindow: false,
  };

  constructor(
    private readonly userService: UserService,
  ) { }

  async getAccountingRoles(): Promise<AccountingRoles> {
    return await this.userService.getUserAccountingRoles().catch(() => ({} as AccountingRoles));
  }

  buildAccountingMenu(roles: AccountingRoles): Menu[] {
    return roles ? [
      this.#createBillingLink(roles),
      this.#createReceivablesLink(roles),
      this.#createPayablesLink(roles),
      this.#createGLLink(roles),
      this.#createReportingLink(roles),
      this.#createConfigurationLink(roles),
    ].filter(link => link) : [];
  }

  buildAccountingReportsLink(roles: AccountingRoles): Menu {
    if (roles.runGLReports || roles.runARandAPReports || roles.runProductionReports || roles.viewPaymentTransactions) {
      return Object.assign({
        label: 'Accounting',
        name: 'LookerReportsAccounting',
        url: globalWindow.UrlResolver.MrFive('looker-reports/category/accounting'),
      }, this.#defaultMenu) as Menu;
    }
    return null;
  }

  #createBillingLink(roles: AccountingRoles): Menu {
    if (roles.billPolicy || roles.viewInvoices) {
      return Object.assign({
        label: 'Billing',
        url: globalWindow.UrlResolver.Accounting('Billing/Invoices'),
      }, this.#defaultMenu) as Menu;
    } else if (roles.applyDirectBillReceipts) {
      return Object.assign({
        label: 'Billing',
        url: globalWindow.UrlResolver.Accounting('Billing/Invoices'),
      }, this.#defaultMenu) as Menu;
    }
    return null;
  }

  #createReceivablesLink(roles: AccountingRoles): Menu {
    if (
      roles.enterReceipts
      || roles.applyAgencyBillReceipts
      || roles.postDirectBillToUncategorizedIncome
      || roles.viewPaymentTransactions
    ) {
      return Object.assign({
        label: 'Receivables',
        url: globalWindow.UrlResolver.MrFive('accounting/receivables'),
      }, this.#defaultMenu) as Menu;
    } else if (roles.createWriteOffs) {
      return Object.assign({
        label: 'Receivables',
        url: globalWindow.UrlResolver.Accounting('Receivables/WriteOff'),
      }, this.#defaultMenu) as Menu;
    } else if (roles.viewInvoices || roles.runARandAPReports) {
      return Object.assign({
        label: 'Receivables',
        url: globalWindow.UrlResolver.Accounting('Receivables/Statement'),
      }, this.#defaultMenu) as Menu;
    }
    return null;
  }

  #createPayablesLink(roles: AccountingRoles): Menu {
    if (roles.createChecks) {
      return Object.assign({
        label: 'Payables',
        url: globalWindow.UrlResolver.MrFive('accounting/payables'),
      }, this.#defaultMenu) as Menu;
    } else if (roles.createWriteOffs) {
      return Object.assign({
        label: 'Payables',
        url: globalWindow.UrlResolver.Accounting('Payables/WriteOff'),
      }, this.#defaultMenu) as Menu;
    }
    return null;
  }

  #createGLLink(roles: AccountingRoles): Menu {
    if (roles.enterJournalEntry) {
      return Object.assign({
        label: 'General Ledger',
        url: globalWindow.UrlResolver.Accounting('GeneralLedger/Postings'),
      }, this.#defaultMenu) as Menu;
    } else if (roles.createAdjustments) {
      return Object.assign({
        label: 'General Ledger',
        url: globalWindow.UrlResolver.Accounting('GeneralLedger/Adjustment'),
      }, this.#defaultMenu) as Menu;
    } else if (roles.enterJournalEntry || roles.runGLReports) {
      return Object.assign({
        label: 'General Ledger',
        url: globalWindow.UrlResolver.Accounting('GeneralLedger/ExportToGL/GLDashboard'),
      }, this.#defaultMenu) as Menu;
    } else if (roles.closeAccountingPeriod) {
      return Object.assign({
        label: 'General Ledger',
        url: globalWindow.UrlResolver.Accounting('GeneralLedger/AccountingPeriod/CloseMonth'),
      }, this.#defaultMenu) as Menu;
    } else if (roles.reopenAccountingPeriod) {
      return Object.assign({
        label: 'General Ledger',
        url: globalWindow.UrlResolver.Accounting('GeneralLedger/AccountingPeriod/ReopenMonth'),
      }, this.#defaultMenu) as Menu;
    }
    return null;
  }

  #createReportingLink(roles: AccountingRoles): Menu {
    if (roles.runGLReports) {
      return Object.assign({
        label: 'Reporting',
        url: globalWindow.UrlResolver.Accounting('Reporting/GeneralLedger'),
      }, this.#defaultMenu) as Menu;
    } else if (roles.runARandAPReports) {
      return Object.assign({
        label: 'Reporting',
        url: globalWindow.UrlResolver.Accounting('Reporting/Receivables'),
      }, this.#defaultMenu) as Menu;
    } else if (roles.runProductionReports) {
      return Object.assign({
        label: 'Reporting',
        url: globalWindow.UrlResolver.Accounting('Reporting/Production'),
      }, this.#defaultMenu) as Menu;
    } else if (roles.managePayees || roles.manageAccountingConfiguration || roles.manageUserPermissions) {
      return Object.assign({
        label: 'Reporting',
        url: globalWindow.UrlResolver.Accounting('Reporting/Configuration'),
      }, this.#defaultMenu) as Menu;
    }
    return null;
  }

  #createConfigurationLink(roles: AccountingRoles): Menu {
    if (roles.managePayees) {
      return Object.assign({
        label: 'Configuration',
        url: globalWindow.UrlResolver.Accounting('Configuration/Payee'),
      }, this.#defaultMenu) as Menu;
    } else if (roles.manageAccountingConfiguration) {
      return Object.assign({
        label: 'Configuration',
        url: globalWindow.UrlResolver.Accounting('Configuration/LineItemType'),
      }, this.#defaultMenu) as Menu;
    } else if (roles.manageUserPermissions) {
      return Object.assign({
        label: 'Configuration',
        url: globalWindow.UrlResolver.Accounting('Configuration/User'),
      }, this.#defaultMenu) as Menu;
    }
    return null;
  }
}
